'use client';

import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/AdminLayout';
import AdminCrudModal from '../../components/AdminCrudModal';
import {
  normalizeErrorMessage,
  normalizeFieldErrors,
  requestWithFallback,
  unpackCollection,
} from '../../services/adminCrudApi';
import './Pasien.css';

const emptyForm = () => ({ kode: '', nama: '', harga: '', aktif: true });

const formatHargaInput = (value) => {
  const digits = String(value).replace(/\D/g, '');
  if (!digits) return '';
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

const normalizeHargaForInput = (value) => {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (!Number.isNaN(n)) return formatHargaInput(String(Math.trunc(n)));
  return formatHargaInput(value);
};

const parseHargaNumber = (value) => {
  const digits = String(value).replace(/\D/g, '');
  return digits ? Number(digits) : NaN;
};

const fmtRupiah = (val) => {
  const n = Number(val);
  if (Number.isNaN(n)) return '-';
  return `Rp ${formatHargaInput(String(Math.trunc(n)))}`;
};

export default function Tarif() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm());
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [notice, setNotice] = useState('');
  const [filterSearchInput, setFilterSearchInput] = useState('');
  const [filterSearch, setFilterSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setFilterSearch(filterSearchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [filterSearchInput]);

  const fetchTarif = useCallback(async () => {
    setLoading(true);
    try {
      const params = { per_page: 100 };
      if (filterSearch) params.search = filterSearch;
      const res = await requestWithFallback([
        { method: 'get', url: '/admin/tarif-tindakan', params },
      ]);
      setList(
        unpackCollection(res.data).sort((a, b) => Number(a.id) - Number(b.id))
      );
    } catch (err) {
      console.error(err);
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [filterSearch]);

  useEffect(() => { fetchTarif(); }, [fetchTarif]);

  const resetForm = () => {
    setForm(emptyForm());
    setEditId(null);
    setErrors({});
    setSubmitError('');
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === 'harga'
        ? formatHargaInput(value)
        : type === 'checkbox' ? checked : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setErrors({});

    const payload = {
      kode: String(form.kode).trim() || `TRF-${Date.now()}`,
      nama: String(form.nama).trim(),
      harga: parseHargaNumber(form.harga),
      aktif: Boolean(form.aktif),
    };

    if (!payload.nama || Number.isNaN(payload.harga)) {
      setSubmitError('Nama dan harga wajib diisi.');
      return;
    }

    setSubmitLoading(true);
    try {
      if (editId) {
        await requestWithFallback([
          { method: 'put', url: `/admin/tarif-tindakan/${editId}`, data: payload },
        ]);
        setNotice('Tarif berhasil diperbarui.');
      } else {
        await requestWithFallback([
          { method: 'post', url: '/admin/tarif-tindakan', data: payload },
        ]);
        setNotice('Tarif berhasil ditambahkan.');
      }
      setShowForm(false);
      resetForm();
      fetchTarif();
    } catch (err) {
      const fieldErrors = normalizeFieldErrors(err);
      if (Object.keys(fieldErrors).length) setErrors(fieldErrors);
      else setSubmitError(normalizeErrorMessage(err, 'Gagal menyimpan tarif.'));
    } finally {
      setSubmitLoading(false);
    }
  };

  const openEdit = (row) => {
    setEditId(row.id);
    setForm({
      kode: row.kode ?? '',
      nama: row.nama ?? '',
      harga: normalizeHargaForInput(row.harga),
      aktif: row.aktif !== false,
    });
    setErrors({});
    setSubmitError('');
    setShowForm(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    setDeleteLoading(true);
    try {
      await requestWithFallback([
        { method: 'delete', url: `/admin/tarif-tindakan/${deleteTarget.id}` },
      ]);
      setDeleteTarget(null);
      setNotice('Tarif berhasil dihapus.');
      fetchTarif();
    } catch (err) {
      console.error(err);
      setSubmitError('Gagal menghapus tarif.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const renderFieldError = (field) =>
    errors[field] ? (
      <small style={{ color: '#EF4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>
        {errors[field][0] ?? errors[field]}
      </small>
    ) : null;

  return (
    <AdminLayout title="">
      
      {/* ── HEADER HALAMAN ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: '800', color: '#111827', margin: 0 }}>Manajemen Tarif</h1>
          <p style={{ color: '#6B7280', margin: '4px 0 0', fontSize: '14px' }}>Kelola tarif tindakan medis klinik gigi</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          style={btnPrimaryStyle}
        >
          + Tambah Tarif
        </button>
      </div>

      {/* ── NOTIFIKASI SUKSES ── */}
      {notice && (
        <div style={{ padding: '12px 16px', backgroundColor: '#D1FAE5', color: '#065F46', borderRadius: '10px', fontSize: '14px', marginBottom: '20px', border: '1px solid #A7F3D0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{notice}</span>
          <button onClick={() => setNotice('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#065F46', fontWeight: 'bold' }}>✕</button>
        </div>
      )}

      {/* ── TOOLBAR / CARI DATA ── */}
      <div style={{ background: '#FFFFFF', borderRadius: '16px', border: '1px solid #E5E7EB', padding: '16px 20px', marginBottom: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ flex: '1', minWidth: '260px', maxWidth: '400px' }}>
          <input
            type="search"
            placeholder="Cari tindakan medis..."
            value={filterSearchInput}
            onChange={(e) => setFilterSearchInput(e.target.value)}
            style={inputStyle}
          />
        </div>
        <div style={{ fontSize: '13px', fontWeight: '600', color: '#6B7280', backgroundColor: '#F3F4F6', padding: '8px 14px', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
          Total Tindakan: {list.length}
        </div>
      </div>

      {/* ── TABEL TARIF ── */}
      <div style={{ background: '#FFFFFF', borderRadius: '16px', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '48px 20px', color: '#6B7280', fontSize: '14px' }}>⏳ Memuat data tarif…</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr style={{ backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                  <th style={{ ...thStyle, width: '60px', textAlign: 'center' }}>No</th>
                  <th style={thStyle}>Nama Tindakan</th>
                  <th style={thStyle}>Estimasi Harga</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Status</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {list.length === 0 ? (
                  <tr>
                    <td colSpan="5" style={{ textAlign: 'center', padding: '48px 20px', color: '#9CA3AF', fontSize: '14px' }}>
                      Belum ada data tarif.
                    </td>
                  </tr>
                ) : (
                  list.map((row, i) => (
                    <tr key={row.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                      <td style={{ ...tdStyle, textAlign: 'center', fontWeight: '600', color: '#6B7280' }}>
                        {i + 1}
                      </td>
                      <td style={tdStyle}>
                        <div style={{ fontWeight: '600', color: '#111827' }}>{row.nama}</div>
                        {row.kode && <div style={{ fontSize: '12px', color: '#9CA3AF' }}>Kode: {row.kode}</div>}
                      </td>
                      <td style={tdStyle}>
                        <span style={{ backgroundColor: '#ECFDF5', color: '#047857', padding: '4px 10px', borderRadius: '6px', fontSize: '13px', fontWeight: '700', border: '1px solid #A7F3D0' }}>
                          Mulai dari {fmtRupiah(row.harga)}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <span style={{
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '600',
                          backgroundColor: row.aktif !== false ? '#D1FAE5' : '#F3F4F6',
                          color: row.aktif !== false ? '#065F46' : '#6B7280',
                        }}>
                          {row.aktif !== false ? 'Aktif' : 'Non-Aktif'}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', gap: '6px', justifyContent: 'flex-end' }}>
                          <button type="button" onClick={() => openEdit(row)} style={btnActionStyle('#2563EB')}>Edit</button>
                          <button type="button" onClick={() => setDeleteTarget(row)} style={btnActionStyle('#EF4444')}>Hapus</button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── MODAL TAMBAH / EDIT TARIF ── */}
      <AdminCrudModal
        open={showForm}
        title={editId ? 'Edit Tarif Tindakan' : 'Tambah Tarif Tindakan'}
        onClose={() => { setShowForm(false); resetForm(); }}
      >
        <div style={{ padding: '4px' }}>
          {submitError && (
            <div style={{ padding: '10px 14px', backgroundColor: '#FEE2E2', color: '#991B1B', borderRadius: '8px', fontSize: '13px', marginBottom: '16px', border: '1px solid #FECACA' }}>
              {submitError}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Nama Tindakan <span style={{ color: '#EF4444' }}>*</span></label>
              <input
                name="nama"
                placeholder="Contoh: Pembersihan Karang Gigi (Scaling)"
                value={form.nama}
                onChange={handleChange}
                required
                maxLength={255}
                style={inputStyle}
              />
              {renderFieldError('nama')}
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Harga Mulai <span style={{ color: '#EF4444' }}>*</span></label>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{
                  padding: '8px 14px',
                  backgroundColor: '#F3F4F6',
                  border: '1px solid #D1D5DB',
                  borderRight: 'none',
                  borderTopLeftRadius: '8px',
                  borderBottomLeftRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '700',
                  color: '#374151'
                }}>
                  Rp
                </span>
                <input
                  type="text"
                  inputMode="numeric"
                  name="harga"
                  placeholder="150.000"
                  value={form.harga}
                  onChange={handleChange}
                  required
                  style={{
                    ...inputStyle,
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    flex: 1
                  }}
                />
              </div>
              {renderFieldError('harga')}
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                <input
                  type="checkbox"
                  name="aktif"
                  checked={form.aktif}
                  onChange={handleChange}
                  style={{ width: '16px', height: '16px', accentColor: '#2563EB', cursor: 'pointer' }}
                />
                Status Tindakan Aktif
              </label>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
              <button
                type="button"
                onClick={() => { setShowForm(false); resetForm(); }}
                disabled={submitLoading}
                style={btnSecondaryStyle}
              >
                Batal
              </button>
              <button
                type="submit"
                disabled={submitLoading}
                style={btnPrimaryStyle}
              >
                {submitLoading ? 'Menyimpan…' : editId ? 'Update Data' : 'Simpan Data'}
              </button>
            </div>
          </form>
        </div>
      </AdminCrudModal>

      {/* ── MODAL KONFIRMASI HAPUS ── */}
      <AdminCrudModal
        open={Boolean(deleteTarget)}
        title="Konfirmasi Hapus Tarif"
        onClose={() => setDeleteTarget(null)}
        size="sm"
      >
        <div style={{ padding: '4px' }}>
          <p style={{ margin: '0 0 16px', color: '#374151', fontSize: '14px' }}>
            Apakah Anda yakin ingin menghapus tarif <strong>{deleteTarget?.nama}</strong>? Tindakan ini tidak dapat dibatalkan.
          </p>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <button
              type="button"
              onClick={() => setDeleteTarget(null)}
              disabled={deleteLoading}
              style={btnSecondaryStyle}
            >
              Batal
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleteLoading}
              style={btnDangerStyle}
            >
              {deleteLoading ? 'Menghapus…' : 'Ya, Hapus'}
            </button>
          </div>
        </div>
      </AdminCrudModal>

    </AdminLayout>
  );
}

// ===================== STYLES =====================
const thStyle = {
  padding: '14px 16px',
  fontWeight: '700',
  fontSize: '12px',
  color: '#4B5563',
  textTransform: 'uppercase',
  letterSpacing: '0.05em'
};

const tdStyle = {
  padding: '14px 16px',
  verticalAlign: 'middle',
  fontSize: '13px',
  color: '#374151'
};

const labelStyle = {
  display: 'block',
  marginBottom: '6px',
  fontSize: '12px',
  fontWeight: '600',
  color: '#374151'
};

const inputStyle = {
  width: '100%',
  padding: '8px 12px',
  borderRadius: '8px',
  border: '1px solid #D1D5DB',
  fontSize: '13px',
  outline: 'none',
  backgroundColor: '#F9FAFB',
  boxSizing: 'border-box'
};

const btnActionStyle = (bgColor) => ({
  padding: '6px 12px',
  backgroundColor: bgColor,
  color: '#FFFFFF',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '12px',
  fontWeight: '600'
});

const btnPrimaryStyle = {
  padding: '9px 16px',
  backgroundColor: '#2563EB',
  color: '#FFFFFF',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: '600'
};

const btnSecondaryStyle = {
  padding: '9px 16px',
  backgroundColor: '#FFFFFF',
  color: '#374151',
  border: '1px solid #D1D5DB',
  borderRadius: '8px',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: '600'
};

const btnDangerStyle = {
  padding: '9px 16px',
  backgroundColor: '#EF4444',
  color: '#FFFFFF',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: '600'
};